import { createAsyncThunk } from "@reduxjs/toolkit";

import { loadDeploymentCurrentRequest } from "Api/organizations/projects/environments/deployments/loadDeploymentCurrent.request";
import logger from "Libs/logger";
import { getProjectId } from "Libs/utils";
import { getCommonError } from "Reducers/sliceFactory";

import {
  isCurrentDeploymentFetchedSelector,
  loadingCurrentDeploymentSelector
} from "../deployment.selectors";
import { addClass, addClassWorker } from "../util";

import type {
  LoadDeploymentCurrentProps,
  LoadDeploymentCurrentPayload
} from "../types";
import type { AsyncThunkOptionType } from "Reducers/types";

export const loadDeploymentCurrent = createAsyncThunk<
  LoadDeploymentCurrentPayload,
  LoadDeploymentCurrentProps,
  AsyncThunkOptionType
>(
  "app/environmentDeployment/current",
  async (
    { organizationId, projectId, environmentId, settings = {} },
    { getState, rejectWithValue }
  ) => {
    try {
      const encodedEnvId = encodeURIComponent(environmentId);

      const projectDescriptionId = getProjectId(getState, projectId)!;
      const deployment = await loadDeploymentCurrentRequest({
        projectId: projectDescriptionId,
        environmentId: encodedEnvId,
        verbose: true,
        // A reload must not resolve with a response to a request issued
        // before the change it is reacting to; `fulfilled` would then store
        // stale data and stamp the environment as fetched (CON-1655).
        fresh: settings.reload
      });
      const services = {
        routes: deployment.routes,
        services: addClass(deployment.services, "service"),
        webapps: addClass(deployment.webapps, "app"),
        workers: addClassWorker(Object.entries(deployment.workers), "worker")
      };

      const schemaProperties =
        deployment?._links?.self?.meta?.get?.responses?.default?.content?.[
          "application/json"
        ]?.schema?.properties;

      return {
        deployment,
        schemas: schemaProperties,
        services
      };
    } catch (error) {
      if (![404, 403].includes((error as { code: number }).code)) {
        const commonError = getCommonError(error).error;
        const errorMessage =
          commonError?.message ??
          "An error occurred while attempting to load deployment.";

        logger(errorMessage, {
          action: "load_deployment_current",
          meta: {
            organizationDescriptionId: organizationId,
            environmentDescriptionId: environmentId,
            projectDescriptionId: projectId
          }
        });
      }

      return rejectWithValue(error as { code: number; message: string });
    }
  },
  {
    condition: (
      { organizationId, projectId, environmentId, settings = {} },
      { getState }
    ) => {
      const { escapeLoading, reload } = settings;
      const state = getState();

      /*
       * Deliberately NOT gated on `environment.has_deployment`: the Redux
       * environment record can be stale (e.g. right after fresh project
       * creation, while the first deploy runs), and a stale `false` silently
       * vetoed the fetch with no retry — leaving the Apps & Services and
       * Allocated resources panels empty until a full page reload (DP-644).
       * The API is the source of truth: a 404 means "no deployment yet" and
       * is stored as such. loadDeploymentNext.thunk.ts skips this gate for
       * the same reason.
       */
      const hasNecessaryProps = !!(
        organizationId &&
        projectId &&
        environmentId
      );
      if (!hasNecessaryProps) {
        return false;
      }

      const loadingCurrentDeployment = loadingCurrentDeploymentSelector(state);

      /*
       * `loadingCurrent` is a single global flag, not per-environment, so any
       * other environment's in-flight fetch drops this one. `reload` overrides
       * it for the same reason `escapeLoading` does: the caller knows the
       * deployment changed, and with the once-per-environment guard below
       * there is no later re-dispatch to recover a dropped refresh — the
       * environment would keep its pre-change data for the session.
       */
      if (!escapeLoading && !reload && loadingCurrentDeployment) {
        return false;
      }

      /*
       * Ask the API once per environment. `fetchedEnvironments` is stamped on
       * success and on a settled rejection, so an environment that never
       * deployed — the endpoint answers 404 indefinitely — is asked once
       * instead of on every re-render. Without this, the Configure resources
       * page re-dispatched the fetch for as long as the tab was open, because
       * its effect re-runs whenever any deployment reference changes and
       * `!currentDeployment` stays true forever on a 404 (CON-1655).
       *
       * Callers that need fresh data after something changed the deployment —
       * a completed deploy activity, a resources update — pass `reload: true`.
       */
      if (
        !reload &&
        isCurrentDeploymentFetchedSelector(state, {
          organizationId,
          projectId,
          environmentId
        })
      ) {
        return false;
      }
    }
  }
);
